import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { beautyServices, wellnessServices, BOOKING_URL } from '../data/services';

export const ServicesPage = () => {
  const [activeCategory, setActiveCategory] = useState('all');
  const [hoveredService, setHoveredService] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const allServices = [
    ...beautyServices.services.map(s => ({ ...s, category: 'beauty' })),
    ...wellnessServices.services.map(s => ({ ...s, category: 'wellness' }))
  ];

  const filteredServices = activeCategory === 'all' 
    ? allServices 
    : allServices.filter(s => s.category === activeCategory);

  return (
    <main data-testid="services-page" className="pt-24 bg-chalk min-h-screen">
      {/* Hero */}
      <section className="py-16 lg:py-24 text-center px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <p className="font-cinzel text-amaranth text-xs tracking-[0.3em] mb-4">
            OUR TREATMENTS
          </p>
          <h1 className="font-serif text-dark text-4xl lg:text-6xl mb-6">
            Services & Treatments
          </h1>
          <p className="text-dark-secondary max-w-2xl mx-auto">
            From rejuvenating aesthetic treatments to transformative wellness therapies, 
            discover the perfect path to reveal your inner goddess.
          </p>
        </motion.div>
      </section>

      {/* Filter Tabs */}
      <section className="px-6 mb-12">
        <div className="max-w-7xl mx-auto flex justify-center gap-4">
          {[
            { id: 'all', label: 'All Services' },
            { id: 'beauty', label: 'Beauty' },
            { id: 'wellness', label: 'Wellness' }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveCategory(tab.id)}
              data-testid={`filter-${tab.id}`}
              className={`px-6 py-2.5 rounded-full text-sm font-medium transition-all ${
                activeCategory === tab.id
                  ? 'bg-amaranth text-white'
                  : 'bg-light text-dark hover:bg-thulian/20'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </section>

      {/* Services Grid */}
      <section className="px-6 pb-24">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredServices.map((service, index) => (
              <motion.div
                key={service.id}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.05 }}
                onMouseEnter={() => setHoveredService(service.id)}
                onMouseLeave={() => setHoveredService(null)}
                data-testid={`service-card-${service.id}`}
                className="group relative bg-light overflow-hidden service-card"
              >
                {/* Image */}
                <div className="aspect-[4/3] overflow-hidden">
                  <img
                    src={service.image}
                    alt={service.name}
                    className={`w-full h-full object-cover transition-transform duration-700 ${
                      hoveredService === service.id ? 'scale-110' : 'scale-100'
                    }`}
                  />
                  {/* Category Badge */}
                  <span className={`absolute top-4 left-4 px-3 py-1 text-xs text-white rounded-full ${
                    service.category === 'beauty' ? 'bg-amaranth' : 'bg-brook'
                  }`}>
                    {service.category === 'beauty' ? 'Beauty' : 'Wellness'}
                  </span>
                </div>

                {/* Content */}
                <div className="p-6">
                  {/* Diamond Icon */}
                  <div className={`w-4 h-4 mb-4 transition-all duration-300 ${
                    hoveredService === service.id 
                      ? 'bg-amaranth rotate-0' 
                      : 'bg-thulian rotate-45'
                  }`} />

                  <h3 className="font-serif text-dark text-xl mb-2">
                    {service.name}
                  </h3>
                  <p className="text-dark-secondary text-sm mb-4">
                    {service.shortDesc}
                  </p>

                  {/* Price */}
                  {service.startingPrice && (
                    <p className="text-amaranth font-medium text-sm mb-4">
                      {service.startingPrice}
                    </p>
                  )}

                  {/* Links */}
                  <div className="flex items-center gap-4">
                    <Link
                      to={`/services/${service.id}`}
                      data-testid={`learn-more-${service.id}`}
                      className="text-dark text-sm font-medium hover:text-amaranth transition-colors inline-flex items-center gap-1"
                    >
                      Learn More
                      <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                    </Link>
                    <a
                      href={BOOKING_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`book-${service.id}`}
                      className="text-thulian text-sm hover:text-amaranth transition-colors"
                    >
                      Book Now
                    </a>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
};

export default ServicesPage;
